import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatCurrency, getRecommendationTypeIcon } from "@/lib/utils";
import { OptimizationRecommendation } from "@shared/schema";
import { ArrowRight, ArrowUpRight, DollarSign, FolderOpen, Lightbulb, Trash, ArrowUpCircle } from "lucide-react";
import { useState } from "react";
import RecommendationDetailDialog from "./recommendation-detail-dialog";

// Create a reusable function to get the icon component based on icon name
function getIconComponent(iconName: string) {
  switch (iconName) {
    case 'DollarSign': return <DollarSign className="h-5 w-5" />;
    case 'FolderOpen': return <FolderOpen className="h-5 w-5" />;
    case 'Trash': return <Trash className="h-5 w-5" />;
    case 'ArrowUpCircle': return <ArrowUpCircle className="h-5 w-5" />;
    default: return <Lightbulb className="h-5 w-5" />;
  }
}

export default function SavingsOpportunities() {
  const [selectedRecommendationId, setSelectedRecommendationId] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data, isLoading, error } = useQuery<OptimizationRecommendation[]>({
    queryKey: ['/api/recommendations'],
  });

  const recommendations: OptimizationRecommendation[] = data || [];
  const totalSavings = recommendations.reduce((sum, rec) => sum + rec.potentialSavings, 0);

  const handleViewRecommendation = (id: number) => {
    setSelectedRecommendationId(id);
    setDialogOpen(true);
  };

  return (
    <>
      <Card className="overflow-hidden h-fit">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle className="text-xl font-bold text-slate-900">Savings Opportunities</CardTitle>
              <p className="text-sm text-slate-600 mt-1">Optimize your spending</p>
            </div>
            {!isLoading && (
              <span className="text-xs font-semibold px-3 py-1.5 bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 rounded-full border border-green-200">
                {recommendations.length} {recommendations.length === 1 ? 'Opportunity' : 'Opportunities'}
              </span>
            )}
          </div>
        </CardHeader>
        
        <CardContent className="pt-0">
          {/* Savings Summary */}
          <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-100">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-green-700">
                {isLoading ? <Skeleton className="h-8 w-24 mx-auto" /> : formatCurrency(totalSavings)}
              </h3>
              <p className="text-sm font-medium text-green-600 mt-1">Potential Monthly Savings</p>
            </div>
          </div>
        
          <div className="space-y-3">
            {isLoading ? (
              [...Array(3)].map((_, index) => (
                <div key={index} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-start space-x-4">
                    <Skeleton className="h-12 w-12 rounded-full flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-3 w-full" />
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                    <Skeleton className="h-4 w-16 flex-shrink-0" />
                  </div>
                </div>
              ))
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-8 text-slate-500">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-3">
                  <ArrowUpRight className="h-6 w-6 text-red-500" />
                </div>
                <p className="text-sm font-medium">Failed to load recommendations</p>
                <p className="text-xs text-slate-400 mt-1">Please try again later</p>
              </div>
            ) : recommendations.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-slate-500">
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                  <Lightbulb className="h-6 w-6 text-slate-400" />
                </div>
                <p className="text-sm font-medium">No savings opportunities found</p>
                <p className="text-xs text-slate-400 mt-1">Your spending looks optimized!</p>
              </div>
            ) : (
              <>
                {recommendations.slice(0, 3).map((recommendation) => {
                  const { iconName, color } = getRecommendationTypeIcon(recommendation.title);
                  
                  return (
                    <div 
                      key={recommendation.id} 
                      className="group border border-slate-200 rounded-lg p-4 hover:border-slate-300 hover:shadow-sm transition-all cursor-pointer bg-white"
                      onClick={() => handleViewRecommendation(recommendation.id)}
                    >
                      <div className="flex items-start space-x-4">
                        {/* Icon */}
                        <div className="flex-shrink-0">
                          <div className={`w-12 h-12 rounded-full ${color} flex items-center justify-center group-hover:scale-105 transition-transform`}>
                            {getIconComponent(iconName)}
                          </div>
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-semibold text-slate-900 group-hover:text-slate-800 transition-colors truncate">
                            {recommendation.title}
                          </h4>
                          <p className="text-xs text-slate-600 mt-1 line-clamp-2 leading-relaxed">
                            {recommendation.description}
                          </p>
                          <div className="mt-3 flex items-center text-xs font-medium text-green-600">
                            <ArrowUpRight className="h-3.5 w-3.5 mr-1 flex-shrink-0" />
                            <span className="truncate">
                              Save {formatCurrency(recommendation.potentialSavings)}/month
                            </span>
                          </div>
                        </div>
                        
                        {/* Action Indicator */}
                        <div className="flex-shrink-0 self-center">
                          <div className="text-primary group-hover:text-primary/80 flex items-center text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                            <span className="hidden sm:inline mr-1">View</span>
                            <ArrowRight className="h-3.5 w-3.5" />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {recommendations.length > 3 && (
                  <div className="pt-2">
                    <Button 
                      asChild
                      variant="outline" 
                      className="w-full py-3 text-sm font-medium text-primary border-primary bg-primary/5 hover:bg-primary/10 rounded-lg transition-colors"
                    >
                      <Link to="/cost-management">
                        View All {recommendations.length} Opportunities
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Link>
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <RecommendationDetailDialog
        recommendationId={selectedRecommendationId}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </>
  );
}
